export const CLING_SPRITE = '/junior-assets/images/Cling.png';

/** Spritesheet 1136×1680 — grilla 8×7 (53 frames), cada celda 142×240 px. */
export const CLING_SHEET = {
  src: CLING_SPRITE,
  cols: 8,
  rows: 7,
  frameWidth: 142,
  frameHeight: 240,
  totalFrames: 53,
  /** Loop de vuelo/canto: fila 1 → fila 2 (índices 0–15). */
  loopStartRow: 0,
  loopEndRow: 1,
  displayScale: 0.64,
  /** Espejo horizontal (personaje mira hacia las notas). */
  flipX: true,
  /** ms por frame del loop (~1 s por ciclo completo). */
  loopFrameMs: 62,
} as const;

/** Índices de frame del loop (fila 1 completa + fila 2 completa). */
export function clingLoopFrames(): number[] {
  const { cols, totalFrames, loopStartRow, loopEndRow } = CLING_SHEET;
  const frames: number[] = [];
  for (let row = loopStartRow; row <= loopEndRow; row++) {
    for (let col = 0; col < cols; col++) {
      const idx = row * cols + col;
      if (idx < totalFrames) frames.push(idx);
    }
  }
  return frames;
}

export const CLING_LOOP_FRAMES = clingLoopFrames();

export function clingLoopLength(): number {
  return CLING_LOOP_FRAMES.length;
}

export function clingDisplaySize(): { width: number; height: number } {
  return {
    width: CLING_SHEET.frameWidth * CLING_SHEET.displayScale,
    height: CLING_SHEET.frameHeight * CLING_SHEET.displayScale,
  };
}

export function clingFramePosition(
  frameIndex: number,
  sheetSrc = CLING_SHEET.src
): {
  col: number;
  row: number;
  bgX: number;
  bgY: number;
  sheetWidth: number;
  sheetHeight: number;
  sheetSrc: string;
} {
  const { cols, frameWidth, frameHeight, displayScale, rows } = CLING_SHEET;
  const col = frameIndex % cols;
  const row = Math.floor(frameIndex / cols);
  const dw = frameWidth * displayScale;
  const dh = frameHeight * displayScale;
  return {
    col,
    row,
    bgX: col * dw,
    bgY: row * dh,
    sheetWidth: cols * dw,
    sheetHeight: rows * dh,
    sheetSrc,
  };
}

/** Frame del spritesheet según posición en el loop (0 … loopLength-1). */
export function clingLoopFrameAt(cycleIndex: number): number {
  const frames = CLING_LOOP_FRAMES;
  return frames[((cycleIndex % frames.length) + frames.length) % frames.length]!;
}

/** Alias histórico — recibe índice de ciclo, no índice de sheet directo. */
export function clingFlyFrame(cycleIndex: number): number {
  return clingLoopFrameAt(cycleIndex);
}

/** Umbral RGB por debajo del cual un píxel se vuelve transparente (fondo negro). */
export const CLING_CHROMA_THRESHOLD = 32;

let processedSheetUrl: string | null = null;
let processedSheetPromise: Promise<string> | null = null;

/**
 * Procesa el PNG una vez: convierte píxeles casi negros en alpha 0.
 * Devuelve data URL cacheada para usar como background del sprite.
 */
export function loadProcessedClingSheet(threshold = CLING_CHROMA_THRESHOLD): Promise<string> {
  if (processedSheetUrl) return Promise.resolve(processedSheetUrl);
  if (processedSheetPromise) return processedSheetPromise;

  processedSheetPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          reject(new Error('canvas 2d unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imageData.data;
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i]!;
          const g = d[i + 1]!;
          const b = d[i + 2]!;
          if (r <= threshold && g <= threshold && b <= threshold) {
            d[i + 3] = 0;
          }
        }
        ctx.putImageData(imageData, 0, 0);
        processedSheetUrl = canvas.toDataURL('image/png');
        resolve(processedSheetUrl);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Cling spritesheet failed to load'));
    img.src = CLING_SPRITE;
  });

  return processedSheetPromise;
}

export function getProcessedClingSheetUrl(): string | null {
  return processedSheetUrl;
}
